import { useTranslation } from "react-i18next";
import { AudioLines, FileText, Globe, Languages, Lock, Music } from "lucide-react";

import { languageLabelKey } from "../../../../shared/i18n/contentLanguages";
import { backgroundTrack } from "../../domain/backgroundTracks";
import {
    Meditation,
    MeditationKind,
    formatDuration,
} from "../../domain/types";
import CopyCount from "./CopyCount";
import MeditationActionsMenu, { type MeditationMenuItem } from "./MeditationActionsMenu";
import MeditationLikeButton from "./MeditationLikeButton";

type Props = {
    meditation: Meditation;
    /** Opens it: the whole entry is the way in. */
    onOpen: () => void;
    /** What stays in view beside it: adding someone else's, for instance. */
    action?: React.ReactNode;
    /** The rest of what can be done with it, behind "⋯". */
    menu?: MeditationMenuItem[];
    /** Shown under everything else, the width of the whole entry. */
    footer?: React.ReactNode;
};

const KIND_ICON: Record<MeditationKind, typeof FileText> = {
    [MeditationKind.TEXT]: FileText,
    [MeditationKind.AUDIO]: AudioLines,
    [MeditationKind.TEXT_AND_AUDIO]: AudioLines,
};

/**
 * One meditation in a library.
 *
 * The three things a therapist scanning for something to use in twenty
 * minutes' time actually needs are on the card: what it is made of, how long
 * it runs, and — in the public library — whose it is and on what terms. The
 * tags are there but quiet; they are how you filter, not how you choose.
 */
export default function MeditationCard({ meditation, onOpen, action, menu = [], footer }: Props) {
    const { t } = useTranslation();
    const KindIcon = KIND_ICON[meditation.kind];
    const track = backgroundTrack(meditation.background_track);

    // Catalogue entries are translated; the therapist's own labels are their
    // words and are shown as they typed them.
    const therapies = [
        ...meditation.therapy_types.map((type) => ({
            key: type,
            label: t(`session.therapyOptions.${type}`),
        })),
        ...(meditation.custom_therapy_types ?? []).map((label) => ({
            key: label,
            label,
        })),
    ];

    /*
     * What the meditation is for, shown beside where it belongs.
     *
     * A therapist scanning a catalogue is looking for something to use with
     * the patient in front of them — insomnia, panic, a body they cannot
     * settle in — rather than for a way of working they already know is
     * theirs. Leaving it off the card meant opening meditations to find out.
     */
    const problems = [
        ...meditation.presenting_problems.map((problem) => ({
            key: problem,
            label: t(`publicDirectory.problems.${problem}`),
        })),
        ...(meditation.custom_presenting_problems ?? []).map((label) => ({
            key: label,
            label,
        })),
    ];

    return (
        <article className="relative flex flex-col gap-3 has-[[aria-haspopup=menu][aria-expanded=true]]:z-20 rounded-2xl border border-border-subtle bg-surface-raised p-4 shadow-theme-xs transition-shadow hover:shadow-theme-md">
            <header className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold text-content-primary">
                        <button
                            type="button"
                            onClick={onOpen}
                            className="rounded-sm text-left after:absolute after:inset-0 after:rounded-2xl after:content-[''] hover:text-accent-text focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-accent"
                        >
                            {meditation.title}
                        </button>
                    </h3>
                    {meditation.author_name && (
                        <p className="flex items-center gap-1.5 text-xs text-content-tertiary">
                            {/*
                              * The author's face, but only the one they already
                              * publish: the API sends a URL exactly when their
                              * public profile shows a photo, so there is no
                              * decision to take here.
                              */}
                            {meditation.author_photo_url && (
                                <img
                                    src={meditation.author_photo_url}
                                    alt=""
                                    loading="lazy"
                                    className="size-5 shrink-0 rounded-full object-cover"
                                />
                            )}
                            <span className="truncate">
                                {t("meditation.byAuthor", {
                                    author: meditation.author_name,
                                })}
                            </span>
                        </p>
                    )}
                </div>
                <span
                    className="flex shrink-0 items-center gap-1 rounded-full bg-surface-sunken px-2 py-1 text-xs font-medium text-content-secondary"
                    title={t(`meditation.kinds.${meditation.kind}`)}
                >
                    <KindIcon size={13} aria-hidden="true" />
                    {t(`meditation.kinds.${meditation.kind}`)}
                </span>
            </header>

            {meditation.description && (
                <p className="line-clamp-2 text-sm text-content-secondary">
                    {meditation.description}
                </p>
            )}

            <dl className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-content-tertiary">
                {/*
                  * Two figures where there are two: the author's own, and the
                  * script's arithmetic. They answer different questions —
                  * "how long is this session" and "how long is this text" —
                  * and showing only the second has the tool contradicting the
                  * therapist who wrote the meditation.
                  */}
                {meditation.declared_duration_seconds ? (
                    <>
                        <div className="flex items-center gap-1">
                            <dt className="sr-only">{t("meditation.duration")}</dt>
                            <dd className="font-medium text-content-secondary">
                                {formatDuration(meditation.declared_duration_seconds)}
                            </dd>
                        </div>
                        <div className="flex items-center gap-1">
                            <dt className="sr-only">
                                {t("meditation.promptedDuration")}
                            </dt>
                            <dd>
                                {t("meditation.fields.byPrompter", {
                                    duration: formatDuration(
                                        meditation.estimated_seconds
                                    ),
                                })}
                            </dd>
                        </div>
                    </>
                ) : (
                    <div className="flex items-center gap-1">
                        <dt className="sr-only">{t("meditation.duration")}</dt>
                        <dd>{formatDuration(meditation.estimated_seconds)}</dd>
                    </div>
                )}
                {/* Only where the author said. A meditation written before the
                    field existed shows nothing rather than a guess. */}
                {meditation.language && (
                    <div className="flex items-center gap-1">
                        <dt className="sr-only">{t("meditation.fields.language")}</dt>
                        <dd className="flex items-center gap-1">
                            <Languages size={12} aria-hidden="true" />
                            {t(languageLabelKey(meditation.language), meditation.language)}
                        </dd>
                    </div>
                )}
                {track.src && (
                    <div className="flex items-center gap-1">
                        <dt className="sr-only">{t("meditation.backgroundTrack")}</dt>
                        <dd className="flex items-center gap-1">
                            <Music size={12} aria-hidden="true" />
                            {t(track.labelKey)}
                        </dd>
                    </div>
                )}
                <div className="flex items-center gap-1">
                    <dt className="sr-only">{t("meditation.visibilityLabel")}</dt>
                    <dd className="flex items-center gap-1">
                        {meditation.visibility === "public" ? (
                            <>
                                <Globe size={12} aria-hidden="true" />
                                {t("meditation.visibility.public")}
                            </>
                        ) : (
                            <>
                                <Lock size={12} aria-hidden="true" />
                                {t("meditation.visibility.private")}
                            </>
                        )}
                    </dd>
                </div>
                {meditation.visibility === "public" && (
                    <div className="flex items-center gap-1">
                        <dt className="sr-only">{t("meditation.licenseLabel")}</dt>
                        <dd>{t(`meditation.licenses.${meditation.license}`)}</dd>
                    </div>
                )}
            </dl>

            {therapies.length > 0 && (
                <ul aria-label={t("meditation.fields.therapyTypes")} className="flex flex-wrap gap-1.5">
                    {therapies.slice(0, 3).map(({ key, label }) => (
                        <li
                            key={key}
                            className="rounded-full bg-accent-tint px-2 py-0.5 text-xs text-accent-on-tint"
                        >
                            {label}
                        </li>
                    ))}
                </ul>
            )}

            {problems.length > 0 && (
                // Quieter than the therapy chips, and under them: the two say
                // different things, and one wall of identical pills says
                // neither.
                <ul
                    aria-label={t("meditation.fields.presentingProblems")}
                    className="flex flex-wrap gap-1.5"
                >
                    {problems.slice(0, 3).map(({ key, label }) => (
                        <li
                            key={key}
                            className="rounded-full bg-surface-sunken px-2 py-0.5 text-xs text-content-secondary"
                        >
                            {label}
                        </li>
                    ))}
                    {problems.length > 3 && (
                        <li className="px-1 py-0.5 text-xs text-content-tertiary">
                            {t("meditation.moreTags", { count: problems.length - 3 })}
                        </li>
                    )}
                </ul>
            )}

            <div className="relative z-10 mt-auto flex items-center justify-between gap-2 pt-1">
                <span className="flex items-center gap-2">
                    <MeditationLikeButton meditation={meditation} />
                    <CopyCount count={meditation.copy_count} />
                </span>
                <div className="ml-auto flex items-center gap-1">
                    {action}
                    <MeditationActionsMenu title={meditation.title} items={menu} />
                </div>
            </div>
            {footer && <div className="relative z-10">{footer}</div>}
        </article>
    );
}
